import { createInertiaApp, router } from '@inertiajs/react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'

// Listen for Inertia navigations and show flash messages as toasts
router.on('navigate', (event) => {
  const flash = event.detail.page.props.flash
  if (flash?.notice) toast.success(flash.notice)
  if (flash?.alert) toast.error(flash.alert)
})

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.jsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.jsx'`)
    }

    return page
  },

  setup({ el, App, props }) {
    // Must match SSR setup exactly — no StrictMode wrapper, no extra elements.
    //
    // Pages rendered with SSR ship pre-rendered HTML inside #app and must be
    // hydrated. Pages rendered WITHOUT SSR (e.g., anything under `app/**`
    // which sets `inertia_config(ssr_enabled: false)`) ship an empty #app
    // container — calling hydrateRoot on an empty container triggers a
    // "server rendered HTML didn't match the client" error even though the
    // result is functionally fine. Detect which we're dealing with.
    if (el.hasChildNodes()) {
      hydrateRoot(el, <App {...props} />)
    } else {
      createRoot(el).render(<App {...props} />)
    }

    // Mount Toaster in a separate DOM node to avoid hydration mismatch
    const toasterEl = document.createElement('div')
    document.body.appendChild(toasterEl)
    createRoot(toasterEl).render(<Toaster position="bottom-right" />)
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      // Both must be false for SSR compatibility — must match SSR entrypoint
      useScriptElementForInitialPage: false,
      useDataInertiaHeadAttribute: false,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
